import type { InstanceIdType } from '#core/schema/defs.ts';

import { type Unit, equals as unitsEqual } from '../units/unit.ts';
import type { SourcePos } from './ast.ts';
import type { Env } from './env.ts';
import { TypeMismatchError } from './errors.ts';
import type { AstInput } from './normalize.ts';
import { DashboardSpec } from './ops/dashboard/value.ts';

// Canonical re-export: `DashboardSpec` is the Value-union member, so
// consumers import it from here (the Value home) alongside the other
// Value kinds, even though the class body lives in the dashboard
// module to keep the tile-shape types co-located.
export { DashboardSpec } from './ops/dashboard/value.ts';

import type { LogicalPlan } from './plan.ts';
import { type Schema, schemaJoin } from './schema/types.ts';

/**
 * Runtime value kinds the engine produces and consumes.
 *
 * Currently covers scalars, vectors, Dataset, Schema, ColumnRef, and
 * DashboardSpec (the V-shape dashboard value returned by the top-level
 * `(dashboard …)` op). The union grows further
 * (QuerySpec, …) as the engine evolves —
 * adding a member here is the trigger for adding it to ops that produce
 * or consume it, a `VALUE_KIND_TABLE` row, and an `expect*` narrower.
 */

export type Scalar = number | string | boolean | null | Date;

/**
 * Cell type: a single column-cell value.
 *
 *   - `Scalar` (number / string / boolean / null / Date) — the
 *     primitive leaf values.
 *   - `UnitCell` — a number boxed with its per-row unit; behaves as its
 *     bare number everywhere except unit propagation.
 *   - `Dataset` — a nested timeseries-ref or other deferred tabular
 *     value; one level of nesting only (a Dataset cell's own cells
 *     must be `Scalar`, enforced by `MaterializedDataset`'s
 *     constructor via `NestedDatasetDepthError`).
 *   - `StructCell` — a plain JS record (e.g. CDF
 *     `{ space, externalId }` instance references); arbitrary depth,
 *     leaves are `Cell` themselves.
 *   - `ArrayCell` — a homogeneous-ish array of cells.
 *
 * `Dataset` here is the abstract supertype — a cell may hold either a
 * `MaterializedDataset` or a `LazyDataset` (the canonical timeseries-
 * ref carrier).
 */
export type Cell = Scalar | UnitCell | Dataset | StructCell | ArrayCell;

/** Plain-record cell (struct columns; CDF instance references). */
export type StructCell = Readonly<{ [key: string]: Cell }>;

/** Array-valued cell. */
export type ArrayCell = readonly Cell[];
/**
 * Unit-bearing numeric cell — a number boxed with its per-row {@link Unit}
 * (the dimension vector from `lib/units/unit.ts`). Plain numbers stay
 * unboxed; only unit-stamped values box, so the common numeric path pays
 * nothing. Ops that merely MOVE cells preserve the box by construction;
 * ops that CREATE numbers (arithmetic folds, aggregation kernels) re-derive
 * the unit at value level via the policies in `schema/unit-propagation.ts`.
 * Units never affect identity or display of the cell itself: `cellKey` /
 * `displayCell` and all comparisons see the bare number (units surface at
 * column level).
 */
export class UnitCell {
  readonly value: number;
  readonly unit: Unit;

  constructor(value: number, unit: Unit) {
    this.value = value;
    this.unit = unit;
  }
}

/** Construct a {@link UnitCell}. */
export const unitCell = (value: number, unit: Unit): UnitCell => new UnitCell(value, unit);

/** Discriminator: is `v` a {@link UnitCell}? */
export const isUnitCell = (v: unknown): v is UnitCell => v instanceof UnitCell;

/**
 * Collapse a `Cell` to a plain displayable scalar: `Date` → epoch ms,
 * `boolean` → its string form, `null`/`undefined` → `null`, a unit-bearing
 * cell → its bare number (units render at column level, never per cell).
 * Nested `Dataset` / struct / array cells pass through as-is and are
 * excluded by the consumer's `typeof` narrowing. SSOT — the dashboard
 * panels and the dashboard validation pass must coerce identically, or a
 * tile could validate yet render nothing (and vice versa).
 */
export const cellScalar = (c: Cell | undefined): number | string | null => {
  if (c instanceof Date) {
    return c.getTime();
  }
  if (isUnitCell(c)) {
    return c.value;
  }
  if (c == null) {
    return null;
  }
  if (typeof c === 'boolean') {
    return String(c);
  }
  return c as number | string;
};
/**
 * Numeric value of a cell for computation: a plain `number` passes through,
 * a {@link UnitCell} unwraps to its bare number, everything else is
 * `undefined`. SSOT numeric narrowing — consumers that previously
 * hand-rolled `typeof c === 'number'` funnel through here so boxed and
 * plain numerics behave identically.
 */
export const numericScalarOf = (c: unknown): number | undefined => {
  if (typeof c === 'number') {
    return c;
  }
  return isUnitCell(c) ? c.value : undefined;
};

/** The per-row unit of a cell, when it carries one. */
export const unitOfCell = (c: unknown): Unit | undefined => (isUnitCell(c) ? c.unit : undefined);
/**
 * The shared per-row unit of a run of cells: when every present (non-null)
 * cell is boxed with the SAME unit, that unit; any mix — differing units,
 * or an unboxed cell alongside boxed ones — is `undefined`. Empty /
 * all-null is `undefined` too; callers that must distinguish "no evidence"
 * from "evidence says none" (e.g. `refineSchema`'s column-unit projection)
 * check for numeric cells first. Shared by the aggregate kernels
 * (group-unified boxing of a numeric reduction) and schema refinement.
 */
export const unifiedUnitOfCells = (cells: readonly unknown[]): Unit | undefined => {
  const [first, ...rest] = cells.filter((c) => c != null).map(unitOfCell);
  if (first === undefined) {
    return undefined;
  }
  return rest.every((u) => u !== undefined && unitsEqual(u, first)) ? first : undefined;
};

/**
 * Box-aware `Number(…)` coercion: a {@link UnitCell} coerces to its bare
 * number instead of `NaN`; everything else keeps exact JS `Number`
 * semantics. SSOT for every numeric-coercion site that may see boxed cells.
 */
export const cellNumber = (v: unknown): number => Number(numericScalarOf(v) ?? v);
/**
 * Box `v` with `unit` when it is a plain number; anything else — null, a
 * string, an already-boxed cell, or an undefined unit — passes through
 * untouched. SSOT for the mint/rebox sites that stamp a resolved unit onto
 * numeric cells (the timeseries provider fetch, the arquero boundary's
 * companion rebox).
 */
export const boxNumericCell = (v: Cell, unit: Unit | undefined): Cell =>
  unit !== undefined && typeof v === 'number' ? unitCell(v, unit) : v;

export type Value =
  | Scalar
  | UnitCell
  | readonly Value[]
  | Dataset
  | Schema
  | ColumnRef
  | DashboardSpec
  | LambdaValue
  | Keyword
  | MapValue;

/**
 * Column-map shape: name → array of `Cell`s. Extracted as an alias
 * because both the `Dataset` field and its constructor parameter mention
 * the same nested type; centralising it keeps the runtime shape and the
 * constructor contract in sync.
 *
 * Cells are `Cell = Scalar | Dataset`. Recursive nesting beyond one level
 * is rejected by `MaterializedDataset`'s constructor (`NestedDatasetDepthError`).
 */
export type Columns = Readonly<Record<string, readonly Cell[]>>;
/**
 * Per-row instance identity for a single dataset row. A row that is
 * backed by a CDF instance carries that instance's `{ space, externalId }`;
 * a synthesised row whose group/join key set does not functionally
 * determine a single instance carries `undefined`.
 */
export type RowIdentity = InstanceIdType | undefined;

/**
 * The dataset's typed identity axis: one {@link RowIdentity} per row,
 * row-aligned with the dataset's columns. `undefined` (rather than an
 * array of `undefined`) signals that the dataset is not instance-backed
 * at all — the fail-loud signal the relation-membership set-ref reads when
 * recovering full {space, externalId} identity from the source.
 */
export type IdentityAxis = readonly RowIdentity[] | undefined;
/**
 * Build one {@link RowIdentity} from a row's `space` / `externalId` cells.
 * Both must be present strings for the row to carry a determinate
 * instance identity; otherwise the row is `undefined`. The single
 * cell-level shape check shared by every synthesis-op identity builder.
 */
const cellIdentity = (space: Cell | undefined, externalId: Cell | undefined): RowIdentity =>
  typeof space === 'string' && typeof externalId === 'string' ? { space, externalId } : undefined;

/**
 * Shared pure predicate: does a group / `:rows` / join key set
 * functionally determine a single instance identity? True IFF the key
 * set includes BOTH `space` AND `externalId` (and both name real
 * columns on `schema`). The SSOT for the synthesized-row identity rule
 * reused by `aggregate` / `pivot` / `time-bucket` / `join`: a row
 * synthesised from a group keyed on both identity columns inherits that
 * group's single instance identity; any other key set yields
 * `undefined` (a space is never fabricated).
 */
export const groupKeysDetermineIdentity = (keys: readonly string[], schema: Schema): boolean =>
  keys.includes('space') && keys.includes('externalId') && schema.has('space') && schema.has('externalId');

/**
 * Synthesise the identity axis for a group-then-emit op's output
 * {@link Columns}, given the key set that produced the groups and the
 * INPUT `schema`. When {@link groupKeysDetermineIdentity} holds, every
 * output row carries the `{space, externalId}` of its group (both are
 * group keys, so they survive verbatim in the output columns); else the
 * whole axis is `undefined`. Row-aligned with `columns` by construction.
 */
export const synthesizeIdentity = (columns: Columns, keys: readonly string[], schema: Schema): IdentityAxis => {
  if (!groupKeysDetermineIdentity(keys, schema)) {
    return undefined;
  }
  const spaces = columns.space ?? [];
  const externalIds = columns.externalId ?? [];
  return spaces.map((space, i) => cellIdentity(space, externalIds[i]));
};

/**
 * Tabular value. `Dataset` is an abstract supertype discriminated into
 * `MaterializedDataset` (carries `columns`) and `LazyDataset` (carries
 * `plan`). Both always carry the dataset's authoritative `Schema` so
 * type-check, propagation,
 * and validation paths don't have to know which kind they're holding.
 *
 * The Schema's insertion order is the canonical column order (relevant
 * for materialised datasets; lazy datasets carry the same schema but
 * defer materialisation to fetch time).
 *
 * Every Dataset also carries a typed `identity` axis (see
 * {@link IdentityAxis}): the row-aligned instance identity propagated
 * through every verb. `undefined` means the dataset is not
 * instance-backed.
 *
 * To narrow an abstract `Dataset` reference, prefer the
 * `isMaterialized(ds)` / `isLazy(ds)` predicates over `instanceof`
 * (both are equivalent runtime checks; the predicates make intent
 * explicit and narrow the TypeScript type via the `state` discriminator).
 *
 * Dataset is the canonical input/output of data-producing ops
 * (`query`, `transform`, …) and the right-hand side of
 * `(def name expr)` bindings.
 */
export abstract class Dataset {
  readonly kind = 'dataset' as const;
  abstract readonly state: 'materialized' | 'lazy';

  constructor(
    readonly schema: Schema,
    readonly identity: IdentityAxis = undefined,
  ) {}
}

/**
 * Materialised Dataset — all columns populated. Pipeline verbs operate
 * exclusively on this kind (the dispatch wrapper routes `LazyDataset`
 * inputs without ever calling a verb's `apply`).
 *
 * The constructor validates four invariants on the
 * `(columns, schema, identity)` triple:
 *   1. All columns have equal length.
 *   2. The Schema's keys exactly match the columns' keys.
 *   3. One level of nesting: a Dataset cell's own cells must be
 *      `Scalar` (rejected with `NestedDatasetDepthError` on depth-2
 *      attempts).
 *   4. When the `identity` axis is present it is row-aligned: its length
 *      equals the dataset's row count. The row count is derived as
 *      `Object.values(columns)[0]?.length ?? identity?.length ?? 0`, so
 *      both `undefined` and an empty axis with empty columns are legal.
 *
 * Downstream code can rely on all four without re-checking.
 */
export class MaterializedDataset extends Dataset {
  readonly state = 'materialized' as const;
  readonly columns: Columns;

  constructor(columns: Columns, schema: Schema, identity: IdentityAxis = undefined) {
    super(schema, identity);
    const lengths = Object.values(columns).map((c) => c.length);
    const distinct = new Set(lengths);
    if (distinct.size > 1) {
      const detail = Object.entries(columns)
        .map(([k, v]) => `${k}=${v.length}`)
        .join(', ');
      throw new TypeError(`Dataset columns must have equal length; got ${detail}`);
    }
    const columnNames = new Set(Object.keys(columns));
    const sameSize = columnNames.size === schema.size;
    const sameKeys = sameSize && [...columnNames].every((n) => schema.has(n));
    if (!sameKeys) {
      const cols = [...columnNames].sort().join(', ') || '(none)';
      const sch = [...schema.keys()].sort().join(', ') || '(none)';
      throw new TypeError(
        `Dataset columns and schema must reference the same column names; columns: ${cols}; schema: ${sch}`,
      );
    }
    // Row count is the single source of truth for the identity-axis
    // length: take the first column's length, else the axis's own length
    // (the empty-columns case), else zero.
    const rowCount = Object.values(columns)[0]?.length ?? identity?.length ?? 0;
    if (identity !== undefined && identity.length !== rowCount) {
      throw new TypeError(
        `Dataset identity axis must be row-aligned; got ${identity.length} identities for ${rowCount} rows`,
      );
    }
    // One-level nesting invariant.
    // Each cell may be a `Dataset`, but a Dataset cell's own cells must
    // be `Scalar`. We inspect the immediate cells: every materialised
    // Dataset we hold was constructed via this same constructor, so any
    // depth-2 violation surfaces here on the outer construction.
    // Lazy Dataset cells are opaque (no `columns` to inspect) — their
    // eventual materialisation is expected to honour the same invariant,
    // checked at materialisation time.
    //
    // This is a defensive construction-time assertion: in a correct
    // engine the invariant holds by construction. It deliberately does
    // NOT trust `schema` (the common synthesised schema is all-`unknown`,
    // so it cannot say which columns are nested). The scan is O(total
    // cells), and runs on every intermediate dataset — including the wide
    // flattened outputs of `unnest`/`time-bucket`. Gate it behind `DEV`
    // so tests/dev builds keep the safety net while production drops the
    // per-construction full traversal entirely.
    if (import.meta.env.DEV) {
      for (const [name, cells] of Object.entries(columns)) {
        const offender = cells.find((c) => c instanceof Dataset && datasetHasDatasetCell(c));
        if (offender !== undefined) {
          throw new NestedDatasetDepthError(name);
        }
      }
    }
    this.columns = columns;
  }
}

/**
 * Lazy Dataset — a deferred dataset reference described by a
 * `LogicalPlan`. Carries the dataset's authoritative `Schema` (so
 * type-check / propagation paths work uniformly across materialised and
 * lazy datasets) but no `columns` — the data is fetched at
 * materialisation time.
 *
 * Invariant (not enforced by the constructor): the `plan`'s last
 * `PlanNode.schemaAfter` equals the dataset's `schema`. Producers
 * maintain this manually.
 */
export class LazyDataset extends Dataset {
  readonly state = 'lazy' as const;
  readonly plan: LogicalPlan;

  constructor(plan: LogicalPlan, schema: Schema) {
    super(schema);
    this.plan = plan;
  }

  /**
   * Force this lazy dataset to a `MaterializedDataset` through the
   * supplied special-form context. The `ctx` argument is typed
   * structurally — any object carrying the unified `materialize(ds)`
   * hook satisfies it, so this method is callable from both top-level
   * special-op and pipeline-verb contexts without an import cycle on
   * `op-meta.ts` / `materialize.ts`.
   */
  materialize(ctx: {
    readonly materialize: (ds: LazyDataset) => Promise<MaterializedDataset>;
  }): Promise<MaterializedDataset> {
    return ctx.materialize(this);
  }
}

/**
 * Predicate: does any cell of `ds` hold a `Dataset`? Helper for the
 * `MaterializedDataset` constructor's one-level-nesting depth guard.
 * Lazy datasets have no inspectable cells, so depth-2 detection
 * short-circuits on lazy via `isMaterialized`.
 */
const datasetHasDatasetCell = (ds: Dataset): boolean =>
  isMaterialized(ds) && Object.values(ds.columns).some((cells) => cells.some((c) => c instanceof Dataset));

/**
 * Thrown by `MaterializedDataset`'s constructor when a column cell
 * contains a `Dataset` whose own cells include `Dataset`s — i.e., a
 * depth-2 nesting attempt. Cells are `Scalar | Dataset` for one level of
 * nesting only; recursive nesting is unsupported until a real use case
 * appears.
 *
 * Extends the native `TypeError` to match the existing Dataset-constructor
 * error idiom (length-mismatch and schema/column-name-mismatch both throw
 * plain `TypeError`s); this error is a data-structure invariant, not an
 * evaluation-time failure, so it deliberately doesn't carry a
 * `SourcePos` and isn't part of the `ExprError` hierarchy.
 */
export class NestedDatasetDepthError extends TypeError {
  constructor(readonly column: string) {
    super(
      `Dataset cell in column \`${column}\` contains a Dataset whose own cells include Datasets; only one level of nesting is supported`,
    );
    this.name = 'NestedDatasetDepthError';
  }
}
/**
 * Reference to a column of a Dataset. Constructed by the polymorphic
 * `(col …)` op and consumed at materialisation / render time. The
 * source `ds` is always a concrete `Dataset` value — binding-name
 * lookups happen at construction time (in `col`'s apply), never
 * deferred into the ColumnRef itself.
 *
 *   - `column` — the top-level column name on `ds`.
 *   - `path` — optional struct sub-path; consumer code walks the path
 *     per-cell against record-typed columns.
 *
 * `Cell = Scalar | Dataset` — column-cells cannot hold `ColumnRef`s.
 * Refs live at the expression/Value layer, never inside column data.
 */
export class ColumnRef {
  readonly kind = 'column-ref' as const;
  constructor(
    readonly ds: Dataset,
    readonly column: string,
    readonly path: readonly string[] = [],
    /**
     * Binding name the column was read from — the head symbol of
     * `(col <binding> …)`, captured at `col` apply time. `undefined` for
     * an expression head or a path-extension ref. The dashboard
     * single-dataset guard groups a tile's columns by this name.
     */
    readonly source?: string,
  ) {
    if (column.length === 0) {
      throw new TypeError('ColumnRef.column must be a non-empty string');
    }
    if (path.some((p) => p.length === 0)) {
      throw new TypeError('ColumnRef.path must not contain empty strings');
    }
  }
}
/**
 * First-class keyword Value. Produced by the evaluator for any
 * `KeywordNode` in non-head position (e.g. `:asc` as a sort direction,
 * `:col-name` as a polymorphic property accessor). Identity-compared
 * by name; the engine never interns keywords, so equality at the Value
 * layer goes through the `.name` field.
 */
export class Keyword {
  readonly kind = 'keyword' as const;
  constructor(readonly name: string) {
    if (name.length === 0) {
      throw new TypeError('Keyword.name must be a non-empty string');
    }
  }
}

/**
 * Map literal Value — `{:k v :k v}` evaluates to one of these. Keys
 * are `Keyword`s (the literal surface admits no other key kind);
 * values are arbitrary `Value`s. Preserves source order via the
 * iteration order of the underlying `Map<string, Value>` — the key
 * map is indexed by keyword `.name` so `.get(:foo)` is O(1).
 */
export class MapValue {
  readonly kind = 'map' as const;
  constructor(readonly entries: ReadonlyMap<string, Value>) {}
}

// Lookup table for `describeValueKind`. Each row pairs a runtime predicate
// with its diagnostic label. Adding a new `Value` kind is a one-row change.
// Order matters: `null` must come before any object/array check
// (`typeof null === 'object'`). `Schema` is matched via `instanceof Map`
// because `Schema = ReadonlyMap<string, ColumnType>`; no other `Value`
// kind is Map-shaped, so the discrimination is unambiguous. Both
// `MaterializedDataset` and `LazyDataset` extend the abstract `Dataset`
// class, so the single `instanceof Dataset` row matches either kind.
const VALUE_KIND_TABLE: readonly { readonly match: (v: Value) => boolean; readonly label: string }[] = [
  { match: (v) => v === null, label: 'null' },
  { match: (v) => typeof v === 'boolean', label: 'boolean' },
  { match: (v) => typeof v === 'number', label: 'number' },
  // A unit-bearing cell IS a number to every diagnostic consumer — the
  // unit is metadata, not a distinct value kind.
  { match: isUnitCell, label: 'number' },
  { match: (v) => typeof v === 'string', label: 'string' },
  { match: (v) => v instanceof Date, label: 'date' },
  { match: (v) => v instanceof Dataset, label: 'dataset' },
  { match: (v) => v instanceof ColumnRef, label: 'column-ref' },
  { match: (v) => v instanceof DashboardSpec, label: 'dashboard-spec' },
  { match: (v) => v instanceof LambdaValue, label: 'lambda' },
  { match: (v) => v instanceof Keyword, label: 'keyword' },
  { match: (v) => v instanceof MapValue, label: 'map' },
  { match: (v) => v instanceof Map, label: 'schema' },
  { match: Array.isArray, label: 'vector' },
];

/**
 * Human-readable name for a runtime kind. Used in diagnostics where we
 * want to tell the caller "I expected a Dataset, got a number".
 * Dispatches via `VALUE_KIND_TABLE` — the first matching predicate's
 * label wins; if none match (a shape outside `Value` — e.g. a struct
 * or array cell handed in by a `Cell`-typed call site), falls back to
 * `'unknown'`. Signature is `unknown` to match the defensive fallback;
 * narrower call sites still type-check through subtyping.
 */
export const describeValueKind = (v: unknown): string =>
  VALUE_KIND_TABLE.find(({ match }) => match(v as Value))?.label ?? 'unknown';

/**
 * Build a `Value → T` narrower from a runtime predicate + an expected-
 * kind label. Centralises the three-line "predicate fails → throw
 * `TypeMismatchError(describeValueKind(v), <expected>, context, pos)`;
 * predicate passes → return as-is" shape that `expectDataset`,
 * `expectSchema`, and `expectColumnRef` all share. Adding a new
 * Value-kind narrower is now a one-liner: write the discriminator
 * predicate and call this factory with the expected-kind label.
 *
 * Sibling of `expectDatasetState` (which adds a second layer for
 * state-narrowing between MaterializedDataset and LazyDataset); the
 * two helpers are intentionally separate because their error messages
 * differ — state-narrowing names the actual dataset state (e.g.
 * `'a lazy Dataset'`) rather than the generic value-kind label.
 *
 * `expected` is the human-readable noun-phrase shown in the error
 * (e.g. `'a Dataset'`); `context` is the op-call fragment; `pos` is
 * the source-position sidecar lookup.
 */
const expectValueKind =
  <T extends Value>(predicate: (v: Value) => v is T, expected: string) =>
  (value: Value, context: string, pos: SourcePos): T => {
    if (!predicate(value)) {
      throw new TypeMismatchError(describeValueKind(value), expected, context, pos);
    }
    return value;
  };

/**
 * Narrow a `Value` to a `Dataset`, throwing `TypeMismatchError` if the
 * runtime kind is anything else. Centralises the
 * "I expected a Dataset; got a ..." failure mode so the expected-kind
 * string `'a Dataset'` lives in one place. Accepts both
 * `MaterializedDataset` and `LazyDataset` — the abstract supertype is
 * what most consumers want. `context` is the op-call fragment shown in
 * the error (e.g. `(def wells ...)`).
 */
export const expectDataset = expectValueKind((v): v is Dataset => v instanceof Dataset, 'a Dataset');
/**
 * Narrow a `Value` to a `Schema`, throwing `TypeMismatchError` if the
 * runtime kind is anything else. Centralises the "I expected a Schema;
 * got a ..." failure mode. `context` is the op-call fragment shown in
 * the error (e.g. `(schema-of wells)`). `Schema` is a `ReadonlyMap`
 * at runtime; no other `Value` kind is Map-shaped, so `instanceof Map`
 * is sufficient discrimination.
 */
export const expectSchema = expectValueKind((v): v is Schema => v instanceof Map, 'a Schema');

/**
 * Discriminator: is `ds` a `MaterializedDataset` (vs a `LazyDataset`)?
 * Prefer over `instanceof MaterializedDataset` at call sites that
 * already have an abstract `Dataset` reference — the predicate makes
 * intent explicit and narrows the TypeScript type via the `state`
 * discriminator.
 */
export const isMaterialized = (ds: Dataset): ds is MaterializedDataset => ds.state === 'materialized';

/** Discriminator: is `ds` a `LazyDataset`? Mirrors `isMaterialized`. */
export const isLazy = (ds: Dataset): ds is LazyDataset => ds.state === 'lazy';
/**
 * Rebuild `ds` carrying a replacement `schema`. The replacement MUST share the
 * dataset's exact column key-set — boundary callers go through
 * {@link promoteSchema} (join after a `schemaRefines` check), which guarantees
 * equal key-sets.
 */
export const withSchema = (ds: Dataset, schema: Schema): Dataset =>
  isMaterialized(ds)
    ? new MaterializedDataset(ds.columns, schema, ds.identity)
    : new LazyDataset((ds as LazyDataset).plan, schema);
/**
 * Promote a declared schema onto `ds`: field-wise most-concrete join of the
 * dataset's own (runtime-refined) schema with the declaration
 * ({@link schemaJoin}), re-stamped via {@link withSchema}. The SINGLE boundary
 * primitive for "authoritative declaration meets runtime evidence" — shared by
 * `materialize`'s replay re-stamp and the query facade's expansion promote so
 * the two boundaries can never diverge on type/unit merge rules (units merge
 * evidence-first inside `columnTypeJoin`). Callers must assert
 * `schemaRefines(ds.schema, declared)` first — the join is total and
 * key-set-preserving only on refinement-compatible pairs.
 */
export const promoteSchema = (ds: Dataset, declared: Schema): Dataset =>
  withSchema(ds, schemaJoin(ds.schema, declared));

/**
 * Internal helper for the state-narrowing pair `expectMaterialized` /
 * `expectLazy`. Both first narrow to `Dataset` (via `expectDataset`),
 * then check the `state` discriminator; the actual/expected labels
 * differ between the two callers. Centralised here so the
 * "expectDataset → predicate → TypeMismatchError" sequence lives in one
 * place — the SSOT for state-discrimination narrowing.
 */
const expectDatasetState = <T extends Dataset>(
  value: Value,
  predicate: (ds: Dataset) => ds is T,
  actual: string,
  expected: string,
  context: string,
  pos: SourcePos,
): T => {
  const ds = expectDataset(value, context, pos);
  if (!predicate(ds)) {
    throw new TypeMismatchError(actual, expected, context, pos);
  }
  return ds;
};

/**
 * Narrow a `Value` to a `MaterializedDataset`, throwing
 * `TypeMismatchError` if it's anything else (including a `LazyDataset`).
 * Use at boundaries that require materialised columns (arquero
 * conversion, verb-apply input, ...). `context` is the op-call fragment
 * shown in the error.
 */
export const expectMaterialized = (value: Value, context: string, pos: SourcePos): MaterializedDataset =>
  expectDatasetState(value, isMaterialized, 'a lazy Dataset', 'a materialized Dataset', context, pos);

/**
 * Narrow a `Value` to a `LazyDataset`, throwing `TypeMismatchError` if
 * it's anything else (including a `MaterializedDataset`). Mirrors
 * `expectMaterialized`.
 */
export const expectLazy = (value: Value, context: string, pos: SourcePos): LazyDataset =>
  expectDatasetState(value, isLazy, 'a materialized Dataset', 'a lazy Dataset', context, pos);
/** Discriminator: is `v` a `ColumnRef` Value? */
export const isColumnRef = (v: Value): v is ColumnRef => v instanceof ColumnRef;
/** Discriminator: is `v` a `DashboardSpec` Value? */
export const isDashboardSpec = (v: Value): v is DashboardSpec => v instanceof DashboardSpec;

/**
 * Narrow a `Value` to a `ColumnRef`, throwing `TypeMismatchError` if
 * the runtime kind is anything else. Mirrors `expectDataset` /
 * `expectSchema` (built from the same `expectValueKind` factory) so
 * the "I expected a ColumnRef; got a ..." failure mode lives in one
 * place.
 */
export const expectColumnRef = expectValueKind(isColumnRef, 'a ColumnRef');
/**
 * Narrow a `Value` to a `DashboardSpec`, throwing `TypeMismatchError`
 * if the runtime kind is anything else. Mirrors `expectColumnRef` —
 * built from the same `expectValueKind` factory so the "I expected a
 * DashboardSpec; got a ..." failure mode lives in one place. Consumed
 * by the chat plugin's `ShowDashboardTool` execute boundary (4c.8) to
 * narrow the evaluated expression result.
 */
export const expectDashboardSpec = expectValueKind(isDashboardSpec, 'a DashboardSpec');
/**
 * Anonymous function value. Built by the `lambda` special form; captures
 * the lexical `Env` at definition site (closure semantics). Invocation
 * extends `closureEnv` with `params → arg-values` (one frame per call,
 * args eagerly evaluated in the caller's env) and evaluates `body`.
 * First-class: assignable to bindings, passable as a kwarg / positional,
 * returnable from another lambda. Compiled inline in expression context
 * via `runtime/arquero.ts::compileExpr`'s lambda branch; evaluated at
 * call site via the evaluator's `LambdaValue` interception in
 * `evaluator.ts::evalOpCall`.
 *
 * `body` is the raw `AstInput` (not pre-compiled) so the lambda can be
 * invoked from both eager and per-row contexts without two compiled
 * forms drifting. `closureEnv` is the immutable `Env` reference — the
 * lambda may reference any name visible at definition site, including
 * names that get shadowed later (closure semantics).
 *
 * Built by `builtins.ts::lambda` (the special form) and registered as
 * an opaque first-class `Value`. The Value union sees `LambdaValue`;
 * `Cell` (column-cell scalar type) does NOT — lambdas live at the
 * expression/Value layer only, never inside column data.
 */
export class LambdaValue {
  readonly kind = 'lambda' as const;
  constructor(
    readonly params: readonly string[],
    readonly body: AstInput,
    readonly closureEnv: Env,
  ) {
    if (new Set(params).size !== params.length) {
      throw new TypeError(`LambdaValue.params has duplicate parameter names: ${params.join(', ')}`);
    }
  }
}

/** Discriminator: is `v` a `LambdaValue`? */
export const isLambda = (v: Value): v is LambdaValue => v instanceof LambdaValue;

/**
 * Narrow a `Value` to a `LambdaValue`, throwing `TypeMismatchError` if
 * the runtime kind is anything else. Mirrors `expectDataset` /
 * `expectColumnRef` (built from the same `expectValueKind` factory) so
 * the "I expected a Lambda; got a ..." failure mode lives in one place.
 */
export const expectLambda = expectValueKind(isLambda, 'a Lambda');
/** Discriminator: is `v` a `Keyword` Value? */
export const isKeyword = (v: Value): v is Keyword => v instanceof Keyword;

/**
 * Narrow a `Value` to a `Keyword`, throwing `TypeMismatchError` if the
 * runtime kind is anything else.
 */
export const expectKeyword = expectValueKind(isKeyword, 'a keyword');

/** Discriminator: is `v` a `MapValue`? */
export const isMapValue = (v: Value): v is MapValue => v instanceof MapValue;

/**
 * Narrow a `Value` to a `MapValue`, throwing `TypeMismatchError` if the
 * runtime kind is anything else.
 */
export const expectMapValue = expectValueKind(isMapValue, 'a map');
